import { useState, useEffect, useCallback } from 'react';
import confetti from 'canvas-confetti';
import { LIGHT, MUTE, FAINT, GHOST, GOLD, EMBER, BASE, SURFACE, DISPLAY, BODY, MONO } from '../lib/constants';
import { Horizon, Mo, Bd, Dp, Label, Ic } from './shared';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Difficulty badge colors with NEON GLOWS - Teal for Easy, Red for Hard
const DIFF_COLORS = { 
  easy: { color: '#00FFCC', bg: 'rgba(0, 255, 204, 0.12)', glow: 'rgba(0, 255, 204, 0.6)' },
  medium: { color: '#00FFCC', bg: 'rgba(0, 255, 204, 0.12)', glow: 'rgba(0, 255, 204, 0.4)' },
  hard: { color: '#FF4D4D', bg: 'rgba(255, 77, 77, 0.12)', glow: 'rgba(255, 77, 77, 0.6)' }
};
const IMPACT_LABELS = { low: 'LOW IMPACT', medium: 'MED IMPACT', high: 'HIGH IMPACT' };
const CAT_LABELS = { negotiate: 'NEGOTIATE', micro: 'MICRO-ACTION', audit: 'AUDIT', knowledge: 'MINDSET', big_move: 'BIG MOVE' };
const CAT_ICONS = {
  negotiate: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={'var(--gold)'} strokeWidth="1.6" strokeLinecap="round"><path d="M22 16.92v3a2 2 0 01-2.18 2 19.79 19.79 0 01-8.63-3.07 19.5 19.5 0 01-6-6 19.79 19.79 0 01-3.07-8.67A2 2 0 014.11 2h3a2 2 0 012 1.72c.127.96.361 1.903.7 2.81a2 2 0 01-.45 2.11L8.09 9.91a16 16 0 006 6l1.27-1.27a2 2 0 012.11-.45c.907.339 1.85.573 2.81.7A2 2 0 0122 16.92z" /></svg>,
  micro: (s = 16) => Ic.coin(s, GOLD),
  audit: (s = 16) => Ic.search(s, GOLD),
  knowledge: (s = 16) => <svg width={s} height={s} viewBox="0 0 24 24" fill="none" stroke={'var(--gold)'} strokeWidth="1.6" strokeLinecap="round"><path d="M12 2a7 7 0 017 7c0 3-2 5-4 7v3H9v-3c-2-2-4-4-4-7a7 7 0 017-7z" /><line x1="9" y1="19" x2="15" y2="19" /></svg>,
  big_move: (s = 16) => Ic.flame(s, EMBER),
};

// Compact card for Home screen
export function DailyChallengeCard({ onViewAll }) {
  const [challenges, setChallenges] = useState([]);
  const [stats, setStats] = useState({ streak_days: 0, total_completed: 0 });
  const [completing, setCompleting] = useState(null); // Track which challenge is being completed

  const fetchData = useCallback(() => {
    fetch(`${API}/challenges/active`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).then(data => setChallenges(Array.isArray(data) ? data : [])).catch(() => setChallenges([]));
    fetch(`${API}/challenges/stats`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}).then(data => { if (data && !data.detail) setStats(data); }).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const complete = async (id) => {
    setCompleting(id);
    
    // Trigger confetti burst - gold and teal colors
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#E8A849', '#00FFCC', '#FFD700', '#14B8A6', '#F2B85C']
    });
    
    // Wait for animation to play
    setTimeout(async () => {
      const res = await fetch(`${API}/challenges/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: id }),
        credentials: 'include'
      });
      const data = await res.json();
      if (data.stats) setStats(data.stats);
      
      // Mark as completed after a brief delay
      setTimeout(() => {
        setChallenges(prev => prev.map(c => c.id === id ? { ...c, completed: true } : c));
        setCompleting(null);
      }, 600);
    }, 100);
  };

  const todayDone = challenges.filter(c => c.completed).length;

  return (
    <div data-testid="daily-challenge-card" style={{ 
      padding: 16, 
      background: 'rgba(255, 255, 255, 0.03)',
      backdropFilter: 'blur(15px)',
      WebkitBackdropFilter: 'blur(15px)',
      border: '1px solid rgba(255, 255, 255, 0.1)',
      boxShadow: '0 10px 30px rgba(0, 0, 0, 0.5)',
      borderRadius: 20
    }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <div style={{ 
            width: 32, 
            height: 32, 
            background: 'linear-gradient(135deg, #00FFCC 0%, #14B8A6 100%)',
            borderRadius: 8,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            boxShadow: '0 0 20px rgba(0, 255, 204, 0.3)'
          }}>
            <span className="str-b">{Ic.flame(14, BASE)}</span>
          </div>
          <Mo style={{ fontSize: 10, fontWeight: 700, color: '#00FFCC', letterSpacing: 2 }}>TODAY'S CHALLENGES</Mo>
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
          <Mo style={{ fontSize: 9, fontWeight: 700, color: '#00FFCC' }}>{todayDone}/3</Mo>
          {stats.streak_days > 0 && (
            <Mo style={{ 
              fontSize: 10, 
              fontWeight: 700,
              color: GOLD,
              padding: '4px 8px',
              background: 'rgba(232, 168, 73, 0.15)',
              borderRadius: 12,
              boxShadow: '0 0 15px rgba(232, 168, 73, 0.3), 0 0 30px rgba(232, 168, 73, 0.15)',
              textShadow: '0 0 10px rgba(232, 168, 73, 0.5)'
            }}>
              🔥 {stats.streak_days}d
            </Mo>
          )}
        </div>
      </div>

      {challenges.slice(0, 3).map(ch => {
        const iconFn = CAT_ICONS[ch.category] || CAT_ICONS.micro;
        const isCompleting = completing === ch.id;
        const diffBadge = DIFF_COLORS[ch.difficulty] || DIFF_COLORS.medium;
        
        return (
          <div 
            key={ch.id} 
            data-testid={`challenge-${ch.id}`} 
            className={ch.completed ? 'challenge-card-complete' : ''}
            style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '12px 0',
              borderBottom: `1px solid rgba(255, 255, 255, 0.05)`, 
              opacity: ch.completed ? 0.5 : 1,
              transition: 'opacity 0.3s ease'
            }}
          >
            <div style={{
              width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: ch.completed ? 'rgba(0, 255, 204, 0.08)' : 'rgba(255, 255, 255, 0.05)',
              borderRadius: 8,
              transition: 'all 0.3s ease'
            }}>
              {ch.completed ? Ic.check(14, '#00FFCC') : iconFn(14)}
            </div>
            <div className="dettio-flex-1">
              <Bd style={{ fontSize: 13, fontWeight: 600, color: ch.completed ? MUTE : LIGHT }}>{ch.title}</Bd>
              <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                {/* Glowing Pill Badge */}
                <span style={{ 
                  fontSize: 8, 
                  fontWeight: 800,
                  letterSpacing: 1,
                  color: diffBadge.color,
                  background: diffBadge.bg,
                  padding: '4px 10px',
                  borderRadius: 12,
                  boxShadow: `0 0 12px ${diffBadge.glow}, 0 0 24px ${diffBadge.glow}40`,
                  textShadow: `0 0 8px ${diffBadge.glow}`
                }}>
                  {ch.difficulty?.toUpperCase()}
                </span>
                <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>{ch.time_mins} min</Mo>
              </div>
            </div>
            {!ch.completed && (
              <button 
                onClick={() => complete(ch.id)} 
                data-testid={`complete-${ch.id}`}
                className={`challenge-done-btn ${isCompleting ? 'challenge-btn-pop challenge-btn-success' : ''}`}
                style={{
                  padding: '6px 12px', 
                  background: isCompleting ? '#00FFCC' : 'transparent', 
                  border: `1px solid ${isCompleting ? '#00FFCC' : 'rgba(0, 255, 204, 0.4)'}`, 
                  color: isCompleting ? '#000' : '#00FFCC',
                  fontFamily: MONO, 
                  fontSize: 9, 
                  fontWeight: 700, 
                  cursor: 'pointer', 
                  letterSpacing: 0.8,
                  borderRadius: 8,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 6,
                  transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                  boxShadow: isCompleting ? '0 0 20px rgba(0, 255, 204, 0.6)' : 'none',
                  transform: 'scale(1)',
                  textShadow: isCompleting ? '0 0 10px rgba(0, 255, 204, 0.8)' : 'none'
                }}
                onMouseEnter={(e) => {
                  if (!isCompleting) {
                    e.currentTarget.style.transform = 'scale(1.05)';
                    e.currentTarget.style.boxShadow = '0 0 15px rgba(0, 255, 204, 0.4)';
                    e.currentTarget.style.background = 'rgba(0, 255, 204, 0.1)';
                  }
                }}
                onMouseLeave={(e) => {
                  if (!isCompleting) {
                    e.currentTarget.style.transform = 'scale(1)';
                    e.currentTarget.style.boxShadow = 'none';
                    e.currentTarget.style.background = 'transparent';
                  }
                }}
              >
                {isCompleting ? (
                  <>
                    {Ic.check(12, '#000')}
                    <span>DONE</span>
                  </>
                ) : (
                  'DONE'
                )}
              </button>
            )}
          </div>
        );
      })}

      <button onClick={onViewAll} data-testid="view-all-challenges-btn" style={{
        width: '100%', marginTop: 10, padding: '10px', background: 'transparent',
        border: `1px solid ${GHOST}`, color: 'var(--mute)', fontFamily: MONO, fontSize: 9,
        fontWeight: 600, cursor: 'pointer', letterSpacing: 0.5
      }}>VIEW ALL CHALLENGES</button>
    </div>
  );
}

// Full challenges screen
export function ChallengesScreen() {
  const [challenges, setChallenges] = useState([]);
  const [stats, setStats] = useState({ streak_days: 0, total_completed: 0, categories: {} });
  const [expanded, setExpanded] = useState({});
  const [completing, setCompleting] = useState(null);

  const fetchData = useCallback(() => {
    fetch(`${API}/challenges/active`, { credentials: 'include' }).then(r => r.ok ? r.json() : []).then(data => setChallenges(Array.isArray(data) ? data : [])).catch(() => setChallenges([]));
    fetch(`${API}/challenges/stats`, { credentials: 'include' }).then(r => r.ok ? r.json() : {}).then(data => { if (data && !data.detail) setStats(data); }).catch(() => {});
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const complete = async (id) => {
    setCompleting(id);
    
    // Trigger confetti burst - gold and teal colors
    confetti({
      particleCount: 100,
      spread: 70,
      origin: { y: 0.6 },
      colors: ['#E8A849', '#00FFCC', '#FFD700', '#14B8A6', '#F2B85C']
    });
    
    setTimeout(async () => {
      const res = await fetch(`${API}/challenges/complete`, {
        method: 'POST', headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ challenge_id: id }),
        credentials: 'include'
      });
      const data = await res.json();
      if (data.stats) setStats(data.stats);
      
      setTimeout(() => {
        setChallenges(prev => prev.map(c => c.id === id ? { ...c, completed: true } : c));
        setCompleting(null);
      }, 600);
    }, 100);
  };

  const todayDone = challenges.filter(c => c.completed).length;

  return (
    <div data-testid="challenges-screen" className="dettio-screen">
      <Horizon intensity={0.5} />
      <div className="dettio-screen__inner">
        {/* Stats */}
        <div style={{ display: 'flex', gap: 6, marginBottom: 24 }}>
          <div style={{ 
            flex: 1, 
            padding: '16px 12px', 
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
            borderRadius: 12,
            boxShadow: stats.streak_days > 0 
              ? '0 0 20px rgba(232, 168, 73, 0.4), 0 0 40px rgba(232, 168, 73, 0.2), 0 8px 32px rgba(0, 0, 0, 0.4)' 
              : '0 8px 32px rgba(0, 0, 0, 0.4)',
            position: 'relative',
            overflow: 'hidden'
          }}>
            {stats.streak_days > 0 && (
              <div style={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 50% 50%, rgba(232, 168, 73, 0.15) 0%, transparent 70%)',
                pointerEvents: 'none'
              }} />
            )}
            <Mo style={{ fontSize: 8, fontWeight: 700, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6, position: 'relative' }}>STREAK</Mo>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 4, position: 'relative' }}>
              <span className="str-b">{Ic.flame(16, stats.streak_days > 0 ? GOLD : GHOST)}</span>
              <Mo style={{ 
                fontSize: 26, 
                fontWeight: 800, 
                color: stats.streak_days > 0 ? GOLD : GHOST,
                textShadow: stats.streak_days > 0 ? '0 0 20px rgba(232, 168, 73, 0.6), 0 0 40px rgba(232, 168, 73, 0.3)' : 'none'
              }}>{stats.streak_days}</Mo>
            </div>
            <Mo style={{ fontSize: 8, fontWeight: 600, color: 'var(--mute)', display: 'block', position: 'relative' }}>DAYS</Mo>
          </div>
          <div style={{ 
            flex: 1, 
            padding: '16px 12px',
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}>
            <Mo style={{ fontSize: 8, fontWeight: 700, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>TODAY</Mo>
            <Mo style={{ fontSize: 26, fontWeight: 800, color: todayDone === 3 ? GOLD : LIGHT }}>{todayDone}<span style={{ fontSize: 14, color: 'var(--mute)' }}>/3</span></Mo>
            <Mo style={{ fontSize: 8, fontWeight: 600, color: todayDone === 3 ? GOLD : MUTE }}>{todayDone === 3 ? 'ALL DONE' : 'REMAINING'}</Mo>
          </div>
          <div style={{ 
            flex: 1, 
            padding: '16px 12px',
            background: 'rgba(255, 255, 255, 0.03)',
            backdropFilter: 'blur(15px)',
            WebkitBackdropFilter: 'blur(15px)',
            border: '1px solid rgba(255, 255, 255, 0.1)',
            textAlign: 'center',
            borderRadius: 12,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
          }}>
            <Mo style={{ fontSize: 8, fontWeight: 700, color: 'var(--mute)', letterSpacing: 1.5, display: 'block', marginBottom: 6 }}>LIFETIME</Mo>
            <Mo style={{ fontSize: 26, fontWeight: 800, color: 'var(--light)' }}>{stats.total_completed}</Mo>
            <Mo style={{ fontSize: 8, fontWeight: 600, color: 'var(--mute)', display: 'block' }}>COMPLETED</Mo>
          </div>
        </div>

        {/* Today's Challenges */}
        <Label>TODAY'S CHALLENGES</Label>
        {challenges.map(ch => {
          const iconFn = CAT_ICONS[ch.category] || CAT_ICONS.micro;
          const isOpen = expanded[ch.id];
          const diffBadge = DIFF_COLORS[ch.difficulty] || DIFF_COLORS.medium;
          const isCompleting = completing === ch.id;
          return (
            <div key={ch.id} data-testid={`challenge-full-${ch.id}`} style={{
              marginBottom: 8, 
              background: ch.completed ? `${GOLD}04` : 'rgba(255, 255, 255, 0.03)',
              backdropFilter: 'blur(15px)',
              WebkitBackdropFilter: 'blur(15px)',
              border: ch.completed ? `1px solid ${GOLD}20` : '1px solid rgba(255, 255, 255, 0.1)',
              borderLeft: `3px solid ${ch.completed ? GOLD : diffBadge.color}`,
              borderRadius: 12,
              boxShadow: '0 8px 32px rgba(0, 0, 0, 0.4)'
            }}>
              <button onClick={() => setExpanded(p => ({ ...p, [ch.id]: !p[ch.id] }))} style={{
                width: '100%', display: 'flex', alignItems: 'center', gap: 10, padding: '14px',
                background: 'transparent', border: 'none', cursor: 'pointer', textAlign: 'left'
              }}>
                <div style={{
                  width: 36, height: 36, flexShrink: 0, display: 'flex', alignItems: 'center', justifyContent: 'center',
                  background: ch.completed ? `${GOLD}12` : 'rgba(255, 255, 255, 0.05)',
                  borderRadius: 8
                }}>
                  {ch.completed ? Ic.check(16, GOLD) : iconFn(16)}
                </div>
                <div className="dettio-flex-1">
                  <Bd style={{ fontSize: 14, fontWeight: 600, color: ch.completed ? MUTE : LIGHT, textDecoration: ch.completed ? 'line-through' : 'none' }}>{ch.title}</Bd>
                  <div style={{ display: 'flex', gap: 6, marginTop: 4, alignItems: 'center' }}>
                    {/* Difficulty Badge */}
                    <span style={{ 
                      fontSize: 8, 
                      fontWeight: 800,
                      letterSpacing: 1,
                      color: diffBadge.color,
                      background: diffBadge.bg,
                      padding: '3px 8px',
                      borderRadius: 8,
                      boxShadow: ch.difficulty === 'hard' ? `0 0 10px ${diffBadge.glow}` : 'none',
                      textShadow: ch.difficulty === 'hard' ? `0 0 8px ${diffBadge.glow}` : 'none'
                    }}>
                      {ch.difficulty?.toUpperCase()}
                    </span>
                    <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>{ch.time_mins} min</Mo>
                    <Mo style={{ fontSize: 7, color: 'var(--ghost)' }}>·</Mo>
                    <Mo style={{ fontSize: 9, color: 'var(--ghost)' }}>{IMPACT_LABELS[ch.impact]}</Mo>
                  </div>
                </div>
                <Mo style={{ fontSize: 14, color: 'var(--mute)', display: 'block' }}>{isOpen ? '−' : '+'}</Mo>
              </button>
              {isOpen && (
                <div className="fade-in" style={{ padding: '0 14px 14px' }}>
                  <Bd style={{ fontSize: 12, color: 'var(--mute)', lineHeight: 1.7, display: 'block', marginBottom: 10 }}>{ch.desc}</Bd>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
                    <Mo style={{ fontSize: 10, color: 'var(--gold)', letterSpacing: 0.5 }}>EST. SAVINGS: {ch.est_savings}</Mo>
                  </div>
                  {!ch.completed && (
                    <button 
                      onClick={() => complete(ch.id)} 
                      data-testid={`complete-full-${ch.id}`}
                      className={`challenge-done-btn ${isCompleting ? 'challenge-btn-pop challenge-btn-success' : ''}`}
                      style={{
                        width: '100%', 
                        padding: '12px', 
                        background: isCompleting ? '#10B981' : GOLD, 
                        border: 'none', 
                        color: '#FFFFFF',
                        fontFamily: MONO, 
                        fontSize: 10, 
                        fontWeight: 700, 
                        cursor: 'pointer', 
                        letterSpacing: 0.8,
                        borderRadius: 8,
                        transition: 'all 0.2s cubic-bezier(0.4, 0, 0.2, 1)',
                        boxShadow: isCompleting ? '0 0 20px rgba(16, 185, 129, 0.5)' : '0 2px 8px rgba(0, 0, 0, 0.2)',
                        transform: 'scale(1)'
                      }}
                      onMouseEnter={(e) => {
                        if (!isCompleting) {
                          e.currentTarget.style.transform = 'scale(1.02)';
                          e.currentTarget.style.boxShadow = '0 4px 12px rgba(232, 168, 73, 0.4)';
                        }
                      }}
                      onMouseLeave={(e) => {
                        if (!isCompleting) {
                          e.currentTarget.style.transform = 'scale(1)';
                          e.currentTarget.style.boxShadow = '0 2px 8px rgba(0, 0, 0, 0.2)';
                        }
                      }}
                    >
                      MARK AS DONE
                    </button>
                  )}
                  {ch.completed && (
                    <div style={{ padding: '10px', background: `${GOLD}08`, textAlign: 'center', borderRadius: 8 }}>
                      <Mo style={{ fontSize: 10, fontWeight: 700, color: 'var(--gold)', letterSpacing: 1 }}>✓ COMPLETED</Mo>
                    </div>
                  )}
                </div>
              )}
            </div>
          );
        })}

        {todayDone === 3 && (
          <div className="fade-in" style={{ marginTop: 16, padding: 16, background: `${GOLD}08`, border: `1px solid ${GOLD}15`, textAlign: 'center' }}>
            <div style={{ marginBottom: 8 }}>{Ic.trophy(24, GOLD)}</div>
            <Dp style={{ fontSize: 16, fontWeight: 800, color: 'var(--gold)', display: 'block', marginBottom: 4 }}>All challenges complete</Dp>
            <Bd style={{ fontSize: 12, color: 'var(--mute)', display: 'block' }}>Come back tomorrow for 3 new ones. Consistency beats intensity.</Bd>
          </div>
        )}

        {/* Category progress */}
        {Object.keys(stats.categories || {}).length > 0 && (
          <>
            <Label>LIFETIME BREAKDOWN</Label>
            {Object.entries(stats.categories).map(([cat, count]) => (
              <div key={cat} style={{ display: 'flex', alignItems: 'center', gap: 10, padding: '10px 0', borderBottom: `1px solid ${FAINT}` }}>
                {(CAT_ICONS[cat] || CAT_ICONS.micro)(14)}
                <Bd style={{ fontSize: 12, fontWeight: 600, color: 'var(--light)', flex: 1 }}>{CAT_LABELS[cat] || cat}</Bd>
                <Mo style={{ fontSize: 12, fontWeight: 700, color: 'var(--gold)' }}>{count}</Mo>
              </div>
            ))}
          </>
        )}

        <div style={{ marginTop: 20, padding: 14, background: 'var(--faint)' }}>
          <Mo style={{ fontSize: 9, fontWeight: 600, color: 'var(--mute)', letterSpacing: 0.5, lineHeight: 1.7 }}>
            New challenges refresh daily. Each one is designed to take 1-15 minutes and create real financial impact. Small actions compound — just like interest, but in your favor.
          </Mo>
        </div>
      </div>
    </div>
  );
}
